// src/modules/manualJournal/components/DailyJournalView.jsx

import React from 'react';
import {
  Card, Table, Space, Button, Popconfirm, Tag, Spin, Empty, message, Modal,
  Form, Input, InputNumber, Select, DatePicker,
} from 'antd';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { deleteTransaction, updateTransaction } from '../api';
import { ACCOUNT_CATEGORIES, getCategoryIcon, TRANSACTION_TYPES } from '../constants';

export default function DailyJournalView({ transactions, loading, journalDate, onTransactionDeleted, onTransactionUpdated }) {
  const [editingId, setEditingId] = React.useState(null);
  const [editingData, setEditingData] = React.useState(null);
  const [detailModal, setDetailModal] = React.useState(null);
  const [editForm] = Form.useForm();
  const [submitting, setSubmitting] = React.useState(false);

  const handleDelete = async (transactionId) => {
    try {
      await deleteTransaction(transactionId);
      message.success('Transaction deleted');
      onTransactionDeleted?.();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleEditClick = (record) => {
    setEditingData({ ...record });
    editForm.setFieldsValue({
      ...record,
      transactionDate: record.transactionDate ? dayjs(record.transactionDate) : null,
    });
    setEditingId(record.transactionId);
  };

  const handleEditSave = async (transactionId) => {
    try {
      const values = await editForm.validateFields();
      setSubmitting(true);
      await updateTransaction(transactionId, {
        ...values,
        transactionDate: values.transactionDate?.format('YYYY-MM-DD'),
      });
      message.success('Transaction updated');
      setEditingId(null);
      onTransactionUpdated?.();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

  const columns = [
    {
      // The transaction date can now differ from the page it is booked on, so this
      // shows the date rather than a clock time (which was always start-of-day).
      title: 'Txn Date',
      dataIndex: 'transactionDate',
      width: 110,
      render: (d) => {
        if (!d) return '—';
        const backDated = journalDate && !dayjs(d).isSame(dayjs(journalDate), 'day');
        return backDated
          ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>{dayjs(d).format('DD/MM/YYYY')}</Tag>
          : dayjs(d).format('DD/MM/YYYY');
      },
    },
    {
      title: 'From',
      dataIndex: 'accountFrom',
      width: 120,
      render: (v) => (
        <Tag>{getCategoryIcon(v)} {ACCOUNT_CATEGORIES.find((c) => c.value === v)?.label}</Tag>
      ),
    },
    {
      title: 'To',
      dataIndex: 'accountTo',
      width: 120,
      render: (v) => (
        <Tag>{getCategoryIcon(v)} {ACCOUNT_CATEGORIES.find((c) => c.value === v)?.label}</Tag>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      width: 120,
      render: (v) => `₹${fmt(v)}`,
      align: 'right',
    },
    {
      title: 'Type',
      dataIndex: 'transactionType',
      width: 100,
      render: (v) => {
        const txnType = TRANSACTION_TYPES.find((t) => t.value === v);
        return txnType ? <Tag color={txnType.color}>{txnType.label}</Tag> : v;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
      width: 200,
    },
    {
      title: 'Actions',
      width: 100,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetailModal(record)}
            title="View"
          />
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            title="Edit"
          />
          <Popconfirm
            title="Delete transaction?"
            onConfirm={() => handleDelete(record.transactionId)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={`Transactions (${transactions?.length || 0})`}
        style={{ marginBottom: 16 }}
      >
        <Spin spinning={loading}>
          {transactions && transactions.length > 0 ? (
            <Table
              columns={columns}
              dataSource={transactions}
              rowKey="transactionId"
              pagination={{ pageSize: 20 }}
              size="small"
              scroll={{ x: 1200 }}
            />
          ) : (
            <Empty description="No transactions for this day" />
          )}
        </Spin>
      </Card>

      {/* Edit Modal */}
      <Modal
        title="Edit Transaction"
        open={editingId !== null}
        onCancel={() => setEditingId(null)}
        onOk={() => handleEditSave(editingId)}
        confirmLoading={submitting}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            label="Transaction Date"
            name="transactionDate"
            rules={[{ required: true }]}
            tooltip="When the activity happened. The day-book page it is booked on does not change."
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" allowClear={false} />
          </Form.Item>

          <Form.Item
            label="From Account"
            name="accountFrom"
            rules={[{ required: true }]}
          >
            <Select
              options={ACCOUNT_CATEGORIES}
            />
          </Form.Item>

          <Form.Item
            label="To Account"
            name="accountTo"
            rules={[{ required: true }]}
          >
            <Select
              options={ACCOUNT_CATEGORIES}
            />
          </Form.Item>

          <Form.Item
            label="Amount"
            name="amount"
            rules={[{ required: true }]}
          >
            <InputNumber
              prefix="₹"
              style={{ width: '100%' }}
              precision={2}
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            label="Type"
            name="transactionType"
          >
            <Select options={TRANSACTION_TYPES} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailModal !== null}
        onCancel={() => setDetailModal(null)}
        footer={null}
      >
        {detailModal && (
          <div>
            <p><strong>ID:</strong> {detailModal.transactionId}</p>
            <p><strong>Date:</strong> {dayjs(detailModal.transactionDate).format('YYYY-MM-DD HH:mm:ss')}</p>
            <p><strong>From:</strong> {ACCOUNT_CATEGORIES.find((c) => c.value === detailModal.accountFrom)?.label}</p>
            <p><strong>To:</strong> {ACCOUNT_CATEGORIES.find((c) => c.value === detailModal.accountTo)?.label}</p>
            <p><strong>Amount:</strong> ₹{fmt(detailModal.amount)}</p>
            <p><strong>Type:</strong> {detailModal.transactionType}</p>
            <p><strong>Description:</strong> {detailModal.description}</p>
            {detailModal.referenceNumber && <p><strong>Reference:</strong> {detailModal.referenceNumber}</p>}
          </div>
        )}
      </Modal>
    </>
  );
}
